import {
  includeCredentials,
  HttpCodes,
  HttpHeaders,
  ContentTypes,
  RequestMethods,
  RequestModeCors,
} from '../constants'

import {tryStringifyByContent, stringifyQuery, IInputQuery} from '../common/query-string'

import {unauthorizedAction} from '../common/unauthorized-request-action'
import {getApiError, createException} from '../common/request-helpers'
import {createApiError} from '../common/api-error'
import {logger} from '@telstra-connect/logger'
import { resolveTargetURL } from '@utils/ogw-util'

export type RequestHeaders = HeadersInit

export interface IBaseRequestParams<T> {
  url: string
  queryString?: IInputQuery
  hostUrl?: string
  headers?: RequestHeaders
  method: RequestMethods
  contentType?: ContentTypes
  body?: T
  processResponse?: <R>(response: Response) => Promise<R>
}

export interface IBaseFormDataRequestParams {
  url: string
  queryString?: IInputQuery
  hostUrl?: string
  headers?: RequestHeaders
  method: RequestMethods
  body: FormData
}

const toHeaders = (headers: RequestHeaders): Headers => {
  return new Headers({
    ...(headers || {}),
  })
}

const decorateQuery = (url: string, queryString?: IInputQuery) => {
  return queryString ? `${url}?${stringifyQuery(queryString)}` : url
}

const formatUrl = (params: {hostUrl?: string; url: string; queryString?: IInputQuery}) => {
  return `${params.hostUrl || resolveTargetURL(params.url)}${decorateQuery(params.url, params.queryString)}`;
}

export const baseRequest = <TResult, TBody = {}>(params: IBaseRequestParams<TBody>) => {
  const {headers: requestHeaders, body: requestBody, contentType, processResponse} = params

  const headers = toHeaders(requestHeaders)
  const body = tryStringifyByContent(requestBody, contentType)
  const url = formatUrl(params)

  const init = {
    headers,
    method: params.method,
    mode: RequestModeCors,
    body,
  }

  const fetchParams = {
    logBodyWhenError: true,
    ...(processResponse && {processResponse}),
  }

  return fetchIt<TResult>(url, init, fetchParams)
}

export const baseFormDataRequest = <TResult>(params: IBaseFormDataRequestParams) => {
  const headers = toHeaders(params.headers)

  const init = {
    headers,
    method: params.method,
    mode: RequestModeCors,
    body: params.body,
  }

  const url = formatUrl(params)
  return fetchIt<TResult>(url, init, {logBodyWhenError: false})
}

interface IFetchItParams {
  logBodyWhenError: boolean
  processResponse?: <R>(response: Response) => Promise<R>
}

const fetchIt = <TResult>(
  url: string,
  init: RequestInit,
  params: IFetchItParams = {logBodyWhenError: true},
): Promise<TResult> => {
  const fetchOptions: RequestInit = {
    ...init,
    credentials: includeCredentials === true ? 'include' : 'omit',
  }
  return fetch(url, fetchOptions)
    .then((response) => {
      if (response.status === HttpCodes.Unauthorized) {
        unauthorizedAction()
        throw createException(response)
      }

      if (response.status > HttpCodes.BadRequest) {
        throw createException(response)
      }

      return response
    })
    .then(
      (response): Promise<TResult> => {
        if (response.status === HttpCodes.BadRequest) {
          return getApiError(response).then((genericError) => {
            throw createException(response, genericError)
          })
        }

        if (response.status === HttpCodes.NoContent) {
          throw createException(response)
        }

        if (params.processResponse) {
          return params.processResponse(response)
        } else {
          const contentType = (response.headers.get(HttpHeaders.ContentType) || '').toLowerCase()
          if (contentType.indexOf(ContentTypes.JSON) !== -1) {
            return response.json()
          }
        }
      },
    )
    .catch((error) => {
      const exception = createApiError(error, {
        url,
        body: init.body,
        headers: init.headers as Headers,
        method: init.method,
      })

      logger().errorAsync({
        errorName: exception.name,
        message: exception.message,
        url,
        ...(params.logBodyWhenError ? {body: init.body} : {}),
        method: init.method,
        headers: exception.headers,
        statusCode: exception.statusCode,
        data: exception.data,
        stack: exception.stack,
      })

      throw error
    })
}
